import { type CaptureContext, type SeverityLevel, captureMessage } from "@sentry/react"

import { fastJsonStringify } from "@/util/json"
import type { JsonValue } from "@/util/types"

type LogLevel = "debug" | "info" | "warn" | "warning" | "error" | "fatal" | "critical"
type NormalizedLogLevel = "debug" | "info" | "warning" | "error" | "fatal"
type CaptureMode = boolean | "auto"
type SentryTagValue = string | number | boolean | null | undefined

export interface LogEventOptions {
  /** Passing an Error exception object will ensure stacktrace is logged. */
  error?: Error
  /** Previous name for `error`; kept for existing frontend call sites. */
  exception?: Error
  /**
   * Whether the event should be printed out to the console.
   */
  log?: boolean
  /** Previous name for `log`; kept for existing frontend call sites. */
  shouldConsole?: boolean
  /**
   * Whether the event should be "captured" in Sentry.
   * In case of `auto`, it will be captured on error/warn, but not on info/debug.
   */
  capture?: CaptureMode
  /** Previous name for `capture`; kept for existing frontend call sites. */
  shouldCapture?: CaptureMode
  /**
   * Any additional data desired to be logged.
   *
   * NOTE: Since we log everything to the console, which in turn shows up in
   * our and customer's FullStory, we should be mindful of not logging
   * sensitive data, such as PII fields.
   */
  extra?: Record<string, unknown>
  fingerprint?: string[]
  /**
   * Tags to be added to the Sentry event.
   */
  tags?: Record<string, SentryTagValue>
  /** Sentry context name used to group the event payload in Sentry. */
  sentryContext?: string
  /** Override Sentry severity without changing the local console level. */
  sentryLevel?: LogLevel
}

/**
 * Log an event to various destinations, for debugging and monitoring.
 *
 * This logging is intended to be used for soft errors, which are errors that
 * have a fallback behavior, or there's a generic error handling in place which
 * is not giving us enough information for debugging or monitoring.
 *
 * == Destinations ==
 *
 * Sentry: for monitoring and debugging.
 *
 * Console: for FullStory of the embedder window, under customer's account.
 */
export function logEvent(
  level: LogLevel,
  message: string,
  options: LogEventOptions = {},
): boolean {
  const normalizedLevel = normalizeLevel(level)
  if (!normalizedLevel) {
    // eslint-disable-next-line no-console
    console.error("OSE: Unknown OSE log level:", level)
    return false
  }

  const {
    extra,
    fingerprint,
    tags,
    sentryContext,
    sentryLevel,
    error: newError,
    exception,
    capture,
    shouldCapture,
    log,
    shouldConsole,
  } = options
  const error = newError ?? exception
  const shouldLog = log ?? shouldConsole ?? true
  const captureMode = capture ?? shouldCapture ?? "auto"
  const normalizedSentryLevel = sentryLevel
    ? (normalizeLevel(sentryLevel) ?? normalizedLevel)
    : normalizedLevel
  const data = buildEventData(message, error, extra)

  if (shouldLog) {
    logToConsole(normalizedLevel, message, data)
  }

  const shouldSendToSentry = shouldCaptureEvent(normalizedLevel, captureMode)
  if (shouldSendToSentry) {
    captureMessage(
      message,
      buildCaptureContext(normalizedSentryLevel, data, {
        fingerprint,
        sentryContext,
        tags,
      }),
    )
  }

  return shouldSendToSentry
}

global.onunhandledrejection = (event: PromiseRejectionEvent) => {
  logEvent("info", `Unhandled promise rejection: ${event.reason}`, {
    capture: false,
    log: false,
    extra: {
      // eslint-disable-next-line @typescript-eslint/no-base-to-string
      "event.promise": String(event.promise),
      "event.reason": fastJsonStringify(event.reason as JsonValue),
    },
  })
}

function normalizeLevel(level: LogLevel): NormalizedLogLevel | undefined {
  switch (level) {
    case "debug":
    case "info":
    case "error":
    case "fatal":
      return level
    case "warn":
    case "warning":
      return "warning"
    case "critical":
      return "fatal"
    default:
      return undefined
  }
}

function buildEventData(
  message: string,
  error: Error | undefined,
  extra: Record<string, unknown> | undefined,
): Record<string, unknown> {
  const data: Record<string, unknown> = { ...extra, message }
  if (error) {
    data["_.exception"] = error
    data["_.exception.name"] = error.name
    data["_.exception.message"] = error.message
    data["_.exception.cause"] = error.cause
    data.error_class ??= error.name
    data.error_message ??= error.message
  }
  return data
}

function logToConsole(
  level: NormalizedLogLevel,
  message: string,
  data: Record<string, unknown>,
) {
  switch (level) {
    case "debug":
      // eslint-disable-next-line no-console
      console.debug(`OSE: ${message}`, data)
      break
    case "info":
      // eslint-disable-next-line no-console
      console.info(`OSE: ${message}`, data)
      break
    case "warning":
      // eslint-disable-next-line no-console
      console.warn(`OSE: ${message}`, data)
      break
    case "error":
    case "fatal":
      // eslint-disable-next-line no-console
      console.error(`OSE: ${message}`, data)
      break
  }
}

function shouldCaptureEvent(level: NormalizedLogLevel, capture: CaptureMode): boolean {
  switch (capture) {
    case true:
      return true
    case false:
      return false
    case "auto":
      return level === "warning" || level === "error" || level === "fatal"
    default:
      return false
  }
}

function buildCaptureContext(
  level: NormalizedLogLevel,
  data: Record<string, unknown>,
  {
    fingerprint,
    sentryContext,
    tags,
  }: Pick<LogEventOptions, "fingerprint" | "sentryContext" | "tags">,
): CaptureContext {
  return {
    contexts: sentryContext ? { [sentryContext]: data } : undefined,
    extra: data,
    fingerprint,
    level: toSentryLevel(level),
    tags,
  }
}

function toSentryLevel(level: NormalizedLogLevel): SeverityLevel {
  return level
}
